// ---------------------------------------------------------------------------
// theme.js — Warm Stone Design System
// Shadow Fund Admin — Helena Global Investment Opportunities
// ---------------------------------------------------------------------------
// SINGLE SOURCE OF TRUTH. Every component imports from here.
// To change the entire app's look, edit only this file.
// ---------------------------------------------------------------------------

const theme = {
  // ── Fonts ──────────────────────────────────────────────────────────────
  fontBody: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  fontDisplay: "'Playfair Display', Georgia, serif",
  fontMono: "'JetBrains Mono', 'SF Mono', 'Fira Code', 'Consolas', monospace",

  // Google Fonts link (inject in Layout.js <head> or index.html)
  fontLink:
    "https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=Playfair+Display:wght@600;700&family=JetBrains+Mono:wght@400;500&display=swap",

  // ── Core Palette ───────────────────────────────────────────────────────
  bg: "#F5F0EB",               // main page background — warm parchment
  bgCard: "#FFFFFF",           // card surfaces
  bgCardHover: "#FDFAF6",      // card hover / subtle highlight
  bgSubtle: "#FAF7F3",         // alternating row, secondary surfaces
  bgInput: "#FFFFFF",          // form inputs

  text: "#2C2825",             // primary text — deep espresso
  textSecondary: "#5C5550",    // secondary text
  textMuted: "#8A8078",        // muted labels, captions
  textDisabled: "#B8B2AA",     // disabled states
  textOnDark: "#F5F0EB",       // text on dark surfaces
  textOnAccent: "#FFFFFF",     // text on accent buttons

  border: "#E2DBD3",           // default borders
  borderLight: "#EDE8E2",      // subtle borders (cards, dividers)
  borderFocus: "#C4873B",      // focus ring color

  // ── Sidebar ────────────────────────────────────────────────────────────
  sidebar: "#2C2825",          // sidebar background — dark cocoa
  sidebarHover: "#3A3532",     // nav item hover
  sidebarText: "#B8B2AA",      // inactive nav text
  sidebarTextHover: "#D4CFC9", // nav text hover
  sidebarActive: "#E8C87A",    // active nav text — warm gold
  sidebarActiveBg: "rgba(232, 200, 122, 0.10)", // active nav bg
  sidebarBorder: "#3A3532",    // sidebar dividers
  sidebarLogo: "#E8C87A",      // logo accent

  // ── Accent (Amber) ────────────────────────────────────────────────────
  accent: "#C4873B",           // primary action color — warm amber
  accentHover: "#B3762E",      // button hover
  accentLight: "rgba(196, 135, 59, 0.08)", // accent tint for backgrounds
  accentBorder: "rgba(196, 135, 59, 0.25)", // accent border

  // ── Semantic Colors ────────────────────────────────────────────────────
  success: "#5B8A5E",          // positive P&L, confirmed states
  successBg: "rgba(91, 138, 94, 0.08)",
  successBorder: "rgba(91, 138, 94, 0.20)",

  warning: "#C49A3B",          // pending, caution
  warningBg: "rgba(196, 154, 59, 0.08)",
  warningBorder: "rgba(196, 154, 59, 0.20)",

  danger: "#B8564B",           // losses, errors, breaks
  dangerBg: "rgba(184, 86, 75, 0.08)",
  dangerBorder: "rgba(184, 86, 75, 0.20)",

  info: "#5E7FA8",             // informational badges
  infoBg: "rgba(94, 127, 168, 0.08)",
  infoBorder: "rgba(94, 127, 168, 0.20)",

  // ── Shadows ────────────────────────────────────────────────────────────
  shadowSm: "0 1px 2px rgba(44, 40, 37, 0.04)",
  shadow: "0 1px 3px rgba(44, 40, 37, 0.06), 0 1px 2px rgba(44, 40, 37, 0.04)",
  shadowMd: "0 4px 6px rgba(44, 40, 37, 0.06), 0 2px 4px rgba(44, 40, 37, 0.04)",
  shadowLg: "0 10px 15px rgba(44, 40, 37, 0.06), 0 4px 6px rgba(44, 40, 37, 0.04)",
  shadowFocus: "0 0 0 3px rgba(196, 135, 59, 0.15)",

  // ── Border Radius ──────────────────────────────────────────────────────
  radiusSm: "4px",
  radius: "8px",
  radiusMd: "10px",
  radiusLg: "14px",
  radiusFull: "9999px",

  // ── Spacing (base 4px grid) ────────────────────────────────────────────
  space: {
    xs: "4px",
    sm: "8px",
    md: "12px",
    lg: "16px",
    xl: "20px",
    xxl: "24px",
    xxxl: "32px",
  },

  // ── Layout Dimensions ──────────────────────────────────────────────────
  sidebarWidth: 240,
  sidebarCollapsed: 64,
  headerHeight: 0,  // no top header bar — sidebar-only layout
  contentMaxWidth: 1400,
  contentPadding: "24px 28px",

  // ── KPI Cards ──────────────────────────────────────────────────────────
  kpiBg: "linear-gradient(135deg, #FDFAF6 0%, #F5EDDF 100%)",
  kpiBorder: "#E8DFD2",

  // ── Table ──────────────────────────────────────────────────────────────
  tableHeaderBg: "#FAF7F3",
  tableRowHover: "#FDFAF6",
  tableStripeBg: "#FCFAF7",

  // ── Transition ─────────────────────────────────────────────────────────
  transition: "all 0.2s ease",
  transitionFast: "all 0.12s ease",
};

export default theme;
